import React from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Button, IconButton, Chip } from "@mui/material";
import { Add, InfoOutlined, ModeEditOutlineTwoTone } from "@mui/icons-material";

import BreadcrumbLayout from "layouts/BreadcrumbLayout";
import { useListSmsTemplatesQuery } from "store/services/smsTemplateApi";

import TableFilter from "components/Shared/AppTable/TableFilter";
import AppTableDateTime from "components/Shared/AppTableDateTime";
import AppTable from "components/Shared/AppTable";
import CustomTabs from "components/Shared/CustomTabs";
import HighlightedText from "components/Shared/HighlightedText";

import { useAppMeta, useTablePagination } from "hooks";

const SmsTemplateListScreen = () => {
  const { currentPage, perPage, handlePageChange, handlePerRowsChange } =
    useTablePagination();

  const [searchParams, setSearchParams] = useSearchParams();

  // Set default tab to "Active" if not present
  React.useEffect(() => {
    if (!searchParams.get("tab")) {
      setSearchParams({ tab: "Active" }, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const activeTab = searchParams.get("tab") || "Active";
  const keyword = searchParams.get("search") || "";

  const breadcrumbs = [
    { title: "Settings", path: "/app/crm/setting/list" },
    { title: "SMS Templates" },
  ];
  const smsTemplateTabs = [
    { label: "Active", value: "Active" },
    { label: "Inactive", value: "Inactive" },
  ];

  const columns = [
      {
      name: "Entity",
      selector: (row) => row.entity?.name,
      cell: (row) => row.entity?.name || "N/A",
    },
    {
      name: "Template Name",
      selector: (row) => row.templateName,
      cell: (row) => (
        <Link
          title={row?.templateName}
          to={`/app/crm/sms-templates/${row?._id}/detail`}
          className="line-clamp-2 hover:text-blue-600 hover:underline dark:hover:text-blue-300"
        >
          {row?.templateName ? (
            <HighlightedText text={row?.templateName} highlight={keyword} />
          ) : (
            "N/A"
          )}
        </Link>
      ),
    },
    {
      name: "Content",
      selector: (row) => row.content,
      cell: (row) => (
        <HighlightedText
          text={
            row?.content?.substring(0, 50) +
              (row?.content?.length > 50 ? "..." : "") || "N/A"
          }
          highlight={keyword}
        />
      ),
    },
    {
      name: "Type",
      selector: (row) => row.type,
      cell: (row) => row?.type || "N/A",
    },
    {
      name: "Process Code",
      selector: (row) => row.applicableFor,
      cell: (row) => row?.applicableFor || "N/A",
    },
    {
      name: "Variables",
      selector: (row) => row.variables,
      cell: (row) =>
        Array.isArray(row.variables) && row.variables.length > 0
          ? row.variables.join(", ")
          : "N/A",
    },
    {
      name: "Created At",
      selector: (row) => row.createdAt,
      cell: (row) => <AppTableDateTime date={row?.createdAt} />,
    },
    {
      name: "Actions",
      cell: (row) => (
        <>
          <Link to={`/app/crm/sms-templates/${row._id}/detail`}>
            <IconButton
              size="small"
              aria-label="View SMS Template"
              color="primary"
            >
              <InfoOutlined />
            </IconButton>
          </Link>
          <Link to={`/app/crm/sms-templates/${row._id}/edit`}>
            <IconButton size="small" aria-label="Edit SMS Template">
              <ModeEditOutlineTwoTone />
            </IconButton>
          </Link>
        </>
      ),
      ignoreRowClick: true,
      right: true,
    },
  ];

  // RTK Query
  const {
    data: smsTemplateRes,
    isLoading,
    isFetching,
    error,
  } = useListSmsTemplatesQuery({
    page: currentPage,
    limit: perPage,
    active: activeTab === "Active",
    keyword,
  });

  useAppMeta(`${activeTab} SMS Templates`);

  return (
    <BreadcrumbLayout breadcrumbs={breadcrumbs} headerActions={<PageActions />}>
      <BreadcrumbLayout.Error error={error} />

      <BreadcrumbLayout.Paper>
        <CustomTabs tabs={smsTemplateTabs} activeTab={activeTab} />

        <TableFilter
          searchLabel="Search SMS Templates"
          disabled={isLoading || isFetching}
        ></TableFilter>

        <AppTable
          columns={columns}
          data={smsTemplateRes?.data ?? []}
          progressPending={isLoading || isFetching}
          paginationServer
          paginationPerPage={perPage}
          paginationTotalRows={smsTemplateRes?.totalItems ?? 0}
          paginationDefaultPage={currentPage}
          onChangePage={handlePageChange}
          onChangeRowsPerPage={handlePerRowsChange}
        />
      </BreadcrumbLayout.Paper>
    </BreadcrumbLayout>
  );
};

export default SmsTemplateListScreen;

const PageActions = () => {
  return (
    <Link to="/app/crm/sms-templates/create">
      <Button startIcon={<Add />} variant="contained">
        New SMS Template
      </Button>
    </Link>
  );
};
